// Parser for the institutions catalog an ADMIN uploads (Extensión →
// Configuración de aspirantes → Instituciones). Accepts a real CSV or a plain
// one-name-per-line text file, since the lists entities publish come in both
// shapes. Only the institution NAME is kept — the config stores a flat array of
// strings, which is what the formación editor offers as options.

// Header names that mark the column holding the institution name. Compared
// accent- and case-insensitively.
const NAME_HEADERS = [
  "institucion", "institucion educativa", "nombre", "nombre institucion",
  "institution", "name", "ies", "nombre ies",
];

const deaccent = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

// Splits one CSV line honouring quoted fields (which may contain the separator).
// `sep` is auto-detected per file, so a European-style `;` export also works.
function splitLine(line, sep) {
  const out = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"') {
        if (line[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else quoted = false;
      } else field += c;
    } else if (c === '"') {
      quoted = true;
    } else if (c === sep) {
      out.push(field);
      field = "";
    } else {
      field += c;
    }
  }
  out.push(field);
  return out.map((f) => f.trim());
}

// The separator that appears most often outside quotes wins; ties keep the comma.
function detectSeparator(sample) {
  const counts = [",", ";", "\t", "|"].map((sep) => [sep, (sample.match(new RegExp(`\\${sep}`, "g")) || []).length]);
  const [best, n] = counts.sort((a, b) => b[1] - a[1])[0];
  return n > 0 ? best : ",";
}

// Parses the file text into a clean, sorted, de-duplicated list of names.
// Returns { names, skipped } — `skipped` counts rows that carried no name, so
// the UI can report an incomplete import instead of silently dropping rows.
export function parseInstitutionsCsv(text) {
  const lines = String(text || "")
    .replace(/^\uFEFF/, "") // Excel writes a BOM
    .split(/\r\n|\n|\r/)
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { names: [], skipped: 0 };

  const sep = detectSeparator(lines.slice(0, 5).join("\n"));
  const first = splitLine(lines[0], sep);
  // A header row is only assumed when one of its cells is a known name header —
  // otherwise a catalog whose first institution happens to sort first would lose
  // an entry.
  const headerIndex = first.findIndex((c) => NAME_HEADERS.includes(deaccent(c)));
  const hasHeader = headerIndex !== -1;
  const column = hasHeader ? headerIndex : 0;
  const rows = hasHeader ? lines.slice(1) : lines;

  const seen = new Set();
  const names = [];
  let skipped = 0;
  for (const line of rows) {
    const cells = splitLine(line, sep);
    const name = (cells[column] || "").replace(/\s+/g, " ").trim();
    if (!name) { skipped += 1; continue; }
    const key = deaccent(name);
    if (seen.has(key)) continue; // duplicates are not an import error
    seen.add(key);
    names.push(name);
  }
  names.sort((a, b) => a.localeCompare(b, "es"));
  return { names, skipped };
}
